// ScheduleTab.jsx — using react-big-calendar (v1.16.2)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, dateFnsLocalizer, Navigate } from 'react-big-calendar';
import { MessageThread } from './messages';
import PatientInsights from '../PatientInsights';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './schedule.css';

// date-fns v2 localizer (Mon-start weeks via en-GB)
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enGB from 'date-fns/locale/en-GB';

const locales = { 'en-GB': enGB };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const HOUR_HEIGHT = 60; // just for initial scroll calc
const JOIN_BEFORE_MIN = 15;
const JOIN_AFTER_MIN  = 60;

// --- NEW: Custom Toolbar Component ---
// This component renders only the navigation buttons and the date label.
const CustomToolbar = ({ label, onNavigate }) => {
  return (
    <div className="rbc-toolbar">
      <span className="rbc-btn-group">
        <button type="button" onClick={() => onNavigate(Navigate.PREVIOUS)}>
          Back
        </button>
        <button type="button" onClick={() => onNavigate(Navigate.TODAY)}>
          Today
        </button>
        <button type="button" onClick={() => onNavigate(Navigate.NEXT)}>
          Next
        </button>
      </span>
      <span className="rbc-toolbar-label">{label}</span>
    </div>
  );
};


const fullPatientName = (patient) =>
  (patient?.first_name || patient?.last_name)
    ? `${patient.first_name ?? ''} ${patient.last_name ?? ''}`.trim()
    : null;

const getPatientInitials = (patient) => {
  if (!patient) return '?';
  const firstName = patient.first_name?.trim() || '';
  const lastName = patient.last_name?.trim() || '';

  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  } else if (firstName) {
    return firstName[0].toUpperCase();
  } else if (lastName) {
    return lastName[0].toUpperCase();
  }
  return '?';
};

const monday = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0,0,0,0);
  const w = d.getDay(); // 0=Sun
  d.setDate(d.getDate() + (w === 0 ? -6 : 1 - w));
  return d;
};
const addDays = (base, days) => { const d = new Date(base); d.setDate(d.getDate() + days); return d; };

function parseUtcish(x) {
  if (x instanceof Date) return x;
  if (typeof x === 'number') return new Date(x);
  if (!x) return new Date(NaN);
  if (/Z$/.test(x)) return new Date(x);
  return new Date(String(x).replace(' ', 'T') + 'Z');
}

function withinActiveWindow(startISO, endISO) {
  const start = parseUtcish(startISO);
  const end   = parseUtcish(endISO);
  const paddedStart = new Date(start.getTime() - JOIN_BEFORE_MIN * 60000);
  const paddedEnd   = new Date(end.getTime()   + JOIN_AFTER_MIN  * 60000);
  const now = new Date();
  return now >= paddedStart && now <= paddedEnd;
}

async function jsonFetch(r) {
  const payload = await r.json().catch(() => ({}));
  if (!r.ok || payload.ok === false) throw new Error(payload.error || r.statusText);
  return payload;
}

async function getCalendarEvents({ apiBase, clinician_uuid, start, end }) {
  const qs = new URLSearchParams({ clinician_uuid, start, end }).toString();
  const url = `${apiBase}/Clinician/consultations/clinician-bookings?${qs}`;
  
  const res = await jsonFetch(await fetch(url));
  
  // 🔍 Log the full payload
  console.log('[getCalendarEvents]', {
    url,
    clinician_uuid,
    start,
    end,
    bookingsCount: res.bookings?.length,
    sample: res.bookings?.[0]
  });

  return res.bookings || [];
}


async function startSession({ apiBase, booking_uuid, clinician_uuid }) {
  const res = await jsonFetch(await fetch(`${apiBase}/Clinician/consultations/bookings/${booking_uuid}/start-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clinician_uuid })
  }));
  return res.session;
}

async function cancelBooking({ apiBase, booking_uuid, patient_identifier }) {
  await jsonFetch(await fetch(`${apiBase}/Clinician/consultations/bookings/${booking_uuid}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ patient_identifier })
  }));
}

async function deleteUnavailability({ apiBase, unavailability_uuid, clinician_uuid }) {
  await jsonFetch(await fetch(`${apiBase}/Clinician/consultations/unavailability/${unavailability_uuid}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clinician_uuid })
  }));
}

function ModalPortal({ children }) {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}

function EventDetailModal({ detail, onClose, onStart, onCancel, onRemove, onPatientInsights, clinician_uuid, clinician_identifier, apiBase }) {
  if (!detail) return null;
  const startLabel = detail.segmentStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const endLabel   = detail.segmentEnd.toLocaleTimeString([],   { hour: '2-digit', minute: '2-digit', hour12: false });
  const dayLabel   = detail.segmentStart.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  const isBooking  = detail.eventType === 'booking';
  const canStart   = isBooking && withinActiveWindow(detail.slot_start_utc, detail.slot_end_utc);

  const patientInitials = isBooking ? getPatientInitials(detail.patient) : null;
  const displayTitle = detail.title || (isBooking ? 'Booking' : 'Unavailable');

  return (
    <ModalPortal>
      <div className="rbcx-modal">
        <div className="rbcx-modal__inner rbcx-modal__inner--redesign rbcx-modal__inner--modern">
          <button className="rbcx-modal__close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>

          <div className="rbcx-modal__layout">
            {/* Left Side - 20% */}
            <div className="rbcx-modal__left-section">
              {/* Header with Appointment Info */}
              <div className="rbcx-modal__appointment-header">
                <div className="rbcx-appointment__top">
                  {isBooking && patientInitials && (
                    <div className="rbcx-avatar rbcx-avatar--large">
                      <span className="rbcx-avatar__text">{patientInitials}</span>
                    </div>
                  )}
                  <div className="rbcx-appointment__info">
                    <div className="rbcx-title rbcx-title--large">
                      {displayTitle}
                    </div>
                    {isBooking && detail.patient_id && (
                      <div className="rbcx-subtitle rbcx-subtitle--prominent">ID: {detail.patient_id}</div>
                    )}
                    <div className="rbcx-appointment__datetime">
                      <span className="rbcx-datetime__day">{dayLabel}</span>
                      <span className="rbcx-datetime__time">{startLabel} - {endLabel}</span>
                    </div>
                  </div>
                </div>

                {/* Show More Data Button */}
                {isBooking && detail.patient?.user_id && (
                  <div className="rbcx-appointment__patient-data">
                    <button
                      className="rbcx-btn rbcx-btn--primary rbcx-btn--icon rbcx-btn--patient-data"
                      onClick={() => onPatientInsights(detail.patient.user_id)}
                      title="View comprehensive patient data and insights"
                    >
                      <span>Patient Insights</span>
                    </button>
                  </div>
                )}

                {detail.notes && (
                  <div className="rbcx-appointment__notes">
                    <div className="rbcx-notes__label">Notes</div>
                    <div className="rbcx-notes__content">{detail.notes}</div>
                  </div>
                )}
              </div>

              {/* Actions Section */}
              <div className="rbcx-modal__actions-section">
                <div className="rbcx-actions__title">Actions</div>
                <div className="rbcx-actions__buttons">
                  {isBooking ? (
                    <>
                      <button
                        className="rbcx-btn rbcx-btn--primary rbcx-btn--action rbcx-btn--icon"
                        onClick={() => onStart(detail)}
                      >
                        <i className="fas fa-video"></i>
                        <span>Start Consultation</span>
                      </button>
                      <button
                        className="rbcx-btn rbcx-btn--danger rbcx-btn--action rbcx-btn--icon rbcx-btn--cancel"
                        onClick={() => onCancel(detail)}
                      >
                        <i className="fas fa-ban"></i>
                        <span>Cancel Consultation</span>
                      </button>
                    </>
                  ) : (
                    <button
                      className="rbcx-btn rbcx-btn--danger rbcx-btn--action rbcx-btn--icon"
                      onClick={() => onRemove(detail)}
                    >
                      <i className="fas fa-trash"></i>
                      <span>Remove Block</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side - Messages - 80% (only if identifier exists) */}
            {isBooking && detail.patient_identifier && (
              <div className="rbcx-modal__messages-panel">
                <div className="rbcx-messages__header">
                  <div className="rbcx-messages__title">
                    <i className="fas fa-comments"></i>
                    <span>Patient Messages</span>
                  </div>
                </div>
                <div className="rbcx-messages__content">
                  <MessageThread
                    clinician_uuid={clinician_uuid}
                    clinician_identifier={clinician_identifier}
                    patient_identifier={detail.patient_identifier}
                    apiBase={apiBase}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default function ScheduleTab({ clinician_uuid, clinician_identifier, apiBase }) {
  const [date, setDate]           = useState(() => new Date());
  const [events, setEvents]       = useState([]);      // RBC events
  const [loading, setLoading]     = useState(true);
  const [detail, setDetail]       = useState(null);
  const [quickMsg, setQuickMsg]   = useState(null); // Will now be an object: { name, identifier }
  const [patientInsights, setPatientInsights] = useState(null);
  const bodyRef                   = useRef(null);

  const fetchForWeek = useMemo(() => async (anchorDate) => {
    setLoading(true);
    try {
      const start = monday(anchorDate).toISOString();
      const end   = addDays(monday(anchorDate), 7).toISOString();
      const data  = await getCalendarEvents({ apiBase, clinician_uuid, start, end });

      const mapped = (data || []).map((ev) => {
        const start = parseUtcish(ev.slot_start_utc);
        const end   = (() => {
          const e = parseUtcish(ev.slot_end_utc);
          return e > start ? e : new Date(start.getTime() + 60 * 1000); // at least 1 min
        })();

        const name = fullPatientName(ev.patient);

        return {
          id: ev.event_id,
          title: ev.event_type === 'booking' ? (name || ev.patient_identifier || 'Booking') : 'Unavailable',
          start,
          end,
          allDay: false,
          resource: ev, // keep full server payload for actions
        };
      });

      setEvents(mapped);
    } catch (e) {
      console.error('Failed to fetch calendar events:', e);
      alert('Could not load schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, clinician_uuid]);

  useEffect(() => { fetchForWeek(date); }, [fetchForWeek, date]);

  // initial scroll to ~07:00 (RBC uses a time-only Date for this)
  const scrollToTime = useMemo(() => new Date(1970, 0, 1, 7, 0, 0), []);
// In ScheduleTab.jsx

// In ScheduleTab.jsx

  const eventPropGetter = (event) => {
    const et = event?.resource?.event_type;
    const style = {
      // --- THIS IS THE CHANGE ---
      // Use your special blue for booking events.
      backgroundColor: et === 'booking' ? '#497caf' : 'rgba(100,116,139,0.9)',
      borderRadius: '6px',
      color: '#fff',
      border: 'none',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      padding: '2px 4px',
      fontSize: '12px',
    };
    return { style, className: et === 'booking' ? 'rbcx-ev--booking' : 'rbcx-ev--unavail' };
  };

  const onSelectEvent = (ev) => {
    const raw = ev.resource || {};
    setDetail({
      ...raw,
      title: ev.title, // The title is now the full name
      segmentStart: ev.start,
      segmentEnd: ev.end,
      eventType: raw.event_type,
    });
  };

  const onStart = async (e) => {
    try {
      const session = await startSession({ apiBase, booking_uuid: e.event_id, clinician_uuid });
      const cfg = {
        session_uuid: session.session_uuid,
        booking_uuid: e.event_id,
        clinician_uuid,
        role: 'clinician',
        iceServers: session.iceServers,
      };
      localStorage.setItem(`meet:${cfg.session_uuid}`, JSON.stringify(cfg));
      window.open(`/meeting/${cfg.session_uuid}`, '_blank', 'noopener,noreferrer');
      setDetail(null);
    } catch (err) {
      console.error('Failed to start session:', err);
      alert(`Error: Could not start the session. ${err.message}`);
    }
  };

  const onCancel = async (e) => {
    if (!window.confirm(`Cancel the booking for ${e.title}?`)) return;
    await cancelBooking({ apiBase, booking_uuid: e.event_id, patient_identifier: e.patient_identifier });
    setDetail(null);
    fetchForWeek(date);
  };

  const onRemoveUnavailability = async (e) => {
    if (!window.confirm('Remove this unavailability block?')) return;
    await deleteUnavailability({ apiBase, unavailability_uuid: e.event_id, clinician_uuid });
    setDetail(null);
    fetchForWeek(date);
  };

  const handleMessage = (e) => {
    setDetail(null);
    setQuickMsg({ name: e.title, identifier: e.patient_identifier });
  };

  const handlePatientInsights = (user_id) => {
    setDetail(null);
    setPatientInsights(user_id);
  };

  return (
    <div className="rbcx-wrap rbcx-bleed" ref={bodyRef}>

      <Calendar
        className="rbcx-calendar"
        localizer={localizer}
        culture="en-GB"
        events={events}
        startAccessor="start"
        endAccessor="end"
        date={date}
        onNavigate={(d) => setDate(d)}
        view="week"
        views={['week']} // Restrict to only week view
        onView={() => {}} // Dummy function to prevent read-only warning
        components={{
          toolbar: CustomToolbar, // Use the custom toolbar
        }}
        showMultiDayTimes
        step={30}
        timeslots={2}
        popup
        scrollToTime={scrollToTime}
        style={{ height: 'var(--schedHeight, 70vh)', width: '100%' }}
        eventPropGetter={eventPropGetter}
        onSelectEvent={onSelectEvent}
      />

      {loading && <div className="rbcx-loading">Loading...</div>}

      {detail && (
        <EventDetailModal
          detail={detail}
          onClose={() => setDetail(null)}
          onStart={onStart}
          onCancel={onCancel}
          onRemove={onRemoveUnavailability}
          onPatientInsights={handlePatientInsights}
          clinician_uuid={clinician_uuid}
          clinician_identifier={clinician_identifier}
          apiBase={apiBase}
        />
      )}

      {quickMsg && (
        <ModalPortal>
          <div className="rbcx-modal">
            <div className="rbcx-modal__inner rbcx-modal__inner--sm rbcx-modal__inner--modern">
              <div className="rbcx-modal__hdr rbcx-modal__hdr--modern">
                <div className="rbcx-modal__title-section">
                  <div className="rbcx-title rbcx-title--modern">Message {quickMsg.name}</div>
                  <div className="rbcx-subtitle">Patient Communications</div>
                </div>
              </div>
              <div className="rbcx-modal__body rbcx-modal__body--modern">
                <MessageThread
                  clinician_uuid={clinician_uuid}
                  clinician_identifier={clinician_identifier}
                  patient_identifier={quickMsg.identifier}
                  apiBase={apiBase}
                />
              </div>
              <div className="rbcx-modal__act rbcx-modal__act--modern">
                <button className="rbcx-btn rbcx-btn--secondary" onClick={() => setQuickMsg(null)}>Close</button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {patientInsights && (
        <ModalPortal>
          <div className="rbcx-modal">
            <div className="rbcx-modal__inner rbcx-modal__inner--fullscreen rbcx-modal__inner--modern">
              <PatientInsights
                userId={patientInsights}
                onClose={() => setPatientInsights(null)}
              />
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}
